import { Clock3, Flame, Leaf, Minus, Plus, Sparkles, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DishImage } from '@/components/DishImage';
import { useCart } from '@/contexts/CartContext';
import { formatKES } from '@/lib/format';
import type { MenuItem, MenuTag } from '@/lib/menu';
import { cn } from '@/lib/utils';

const TAG_STYLES: Record<MenuTag, { label: string; icon: typeof Star; className: string }> = {
  popular: {
    label: 'Popular',
    icon: Star,
    className: 'bg-marigold text-accent-foreground',
  },
  spicy: {
    label: 'Spicy',
    icon: Flame,
    className: 'bg-ember text-primary-foreground',
  },
  vegetarian: {
    label: 'Veg',
    icon: Leaf,
    className: 'bg-leaf text-secondary-foreground',
  },
  new: {
    label: 'New',
    icon: Sparkles,
    className: 'bg-card text-foreground border border-border',
  },
};

export function MenuItemCard({ item }: { item: MenuItem }) {
  const { addItem, setQty, getQty } = useCart();
  const qty = getQty(item.id);

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-ember/10">
      <div className="relative overflow-hidden">
        <DishImage
          src={item.image}
          alt={item.name}
          className="aspect-[4/3] w-full transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {item.tags.map((tag) => {
            const meta = TAG_STYLES[tag];
            const Icon = meta.icon;
            return (
              <Badge
                key={tag}
                className={cn('gap-1 border-0 px-2 py-0.5 text-[11px] font-bold shadow-sm', meta.className)}
              >
                <Icon className="size-3" />
                {meta.label}
              </Badge>
            );
          })}
        </div>
        <div className="absolute bottom-3 right-3 rounded-full bg-background/95 px-3 py-1 text-sm font-black text-foreground shadow-md backdrop-blur">
          {formatKES(item.price)}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-bold leading-snug">{item.name}</h3>
        </div>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
          {item.description}
        </p>

        <div className="mt-4 flex items-center justify-between gap-3 pt-1">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Clock3 className="size-3.5" />
            {item.prepMinutes} min
          </span>

          {qty === 0 ? (
            <Button
              size="sm"
              onClick={() => addItem(item)}
              className="rounded-full px-4 font-bold shadow-sm shadow-primary/25"
              aria-label={`Add ${item.name} to cart`}
            >
              <Plus className="size-4" />
              Add
            </Button>
          ) : (
            <div className="flex items-center gap-1 rounded-full bg-muted p-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setQty(item.id, qty - 1)}
                className="size-7 rounded-full"
                aria-label={`Remove one ${item.name}`}
              >
                <Minus className="size-3.5" />
              </Button>
              <span className="min-w-6 text-center text-sm font-black tabular-nums" aria-live="polite">
                {qty}
              </span>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setQty(item.id, qty + 1)}
                className="size-7 rounded-full"
                aria-label={`Add one more ${item.name}`}
              >
                <Plus className="size-3.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
